import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import {customFetcher} from './customFetcher';
import {IInvoice, IUpdateInvoicePayload} from "../Interfaces/IInvoice";

/**
 * Fetch all invoices
 */
export const useGetInvoices = (enabled: boolean = true) => {
    const {data: invoices = [], isLoading: isLoadingInvoices} = useQuery<IInvoice[]>({
        queryKey: ['invoices'],
        queryFn: () => customFetcher<IInvoice[]>({endpoint: '/invoices/'}),
        refetchOnWindowFocus: false,
        enabled,
    });
    return {invoiceArray: invoices, isLoadingInvoices: isLoadingInvoices};
};

/**
 * Create a new invoice
 */

export const useCreateInvoice = () => {
    const queryClient = useQueryClient()
    const { mutateAsync: createInvoice } = useMutation<
        IInvoice,
        Error,
        Omit<IInvoice, 'id' | 'total' | 'purpose' | 'created_at'>
    >({
        mutationFn: (payload) =>
            customFetcher<IInvoice>({
                endpoint: '/invoices/',
                method: 'POST',
                body: payload,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['invoices'] })
        },
    })
    return { createInvoice }
}


/**
 * Update an existing invoice
 */
export const useUpdateInvoice = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: updateInvoice} = useMutation<
        IUpdateInvoicePayload,
        Error,
        IUpdateInvoicePayload
    >({
        mutationFn: (updatedInvoice) =>
            customFetcher<IUpdateInvoicePayload>({
                endpoint: `/invoices/${updatedInvoice.id}/`,
                method: 'PUT',
                body: updatedInvoice,
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: ['invoices']});
        },
    });

    return {updateInvoice};
};

/**
 * Delete an invoice by ID
 */
export const useDeleteInvoice = () => {
    const queryClient = useQueryClient();
    const {mutateAsync: deleteInvoice} = useMutation<
        void,
        Error,
        number
    >({
        mutationFn: (id) =>
            customFetcher<void>({
                endpoint: `/invoices/${id}/`,
                method: 'DELETE',
            }),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: ['invoices']});
        },
    });

    return {deleteInvoice};
};
